import { Input, Stack, Text, Select, InputRightAddon, InputLeftAddon, InputGroup } from "@chakra-ui/react";

// Allow for units to have a display prefix, e.g. $ for USD
const MONEY_OPTIONS = [
  {
    prefix: "$",
    value: "USD",
  },
  {
    prefix: "€",
    value: "EUR",
  },
  {
    prefix: "£",
    value: "GBP",
  },
];

const WEIGHT_OPTIONS = [
  {
    value: "kg",
  },
  {
    value: "tonnes", // 'tonnes' clarifies that it means metric tons (not US short tons)
  },
  {
    value: "metric tons C02e", // effectively equivalent to "tonnes" but explicit on CO2e -- which can make a difference if talking about eg methane
  },
];

/**
 *
 * @param {string} metricType
 * @returns [{value, prefix?}]
 */
export function getMetricOptions(metricType) {
  if (metricType === "money") {
    return MONEY_OPTIONS;
  }
  return WEIGHT_OPTIONS;
}
export function getDisplayUnit(metricType, unit) {
  if (!unit) {
    return "";
  }
  if (metricType === "money") {
    return MONEY_OPTIONS.find((o) => o.value === unit)?.prefix || unit;
  }
  return unit;
}

/**
 * For number + unit, e.g. 1000kg or £100
 *
 * @param {string} metricType e.g. "money" or "weight" -- determines the unit options
 * @param {string?} unit Is the unit fixed?
 * @param {string?} unitValue e.g. "kg" or "USD" (which is displayed as $)
 * @param {number?} numericValue
 * @param {function} onChange onChange(event) where event.target.value is {numericValue, unitValue}
 * @param {boolean} readOnly
 * @param {string} reason why the value is disabled or read-only (e.g. "auto-calculated")
 */
export default function MetricInput({
  metricType,
  unit,
  unitValue,
  numericValue,
  onChange,
  readOnly,
  disabled,
  reason,
}) {
  if (numericValue) numericValue = parseFloat(numericValue);
  if (!unitValue) {
    unitValue = unit;
  }
  let prefixUnit = false;
  // unset if unit is fixed
  let unitOptions;
  if (!unit) {
    unitOptions = getMetricOptions(metricType);
  }
  if (metricType === "money") {
    prefixUnit = true;
  }
  const displayUnit = getDisplayUnit(metricType, unitValue);

  const onChangeUnit = (e) => {
    const newUnit = e.target.value;
    onChange({ target: { value: { unitValue: newUnit, numericValue: numericValue } } }); // send an ersatz event
  };
  const onChangeNumber = (e) => {
    const newNumericValue = e.target.value;
    onChange({ target: { value: { unitValue: unitValue, numericValue: newNumericValue } } }); // send an ersatz event
  };

  const $unitSelect = unit ? null : unitOptions ? (
    <Select value={unitValue} onChange={onChangeUnit} border="none" isDisabled={disabled} isReadOnly={readOnly}>
      {unitOptions.map((option) => (
        <option key={option.value} value={option.value}>
          {option.prefix || option.value}
        </option>
      ))}
    </Select>
  ) : (
    <Input onChange={onChangeUnit} isDisabled={disabled} isReadOnly={readOnly} />
  );

  return (
    <>
      <InputGroup title={reason}>
        {prefixUnit && <InputLeftAddon>{unit ? <Text>{displayUnit}</Text> : $unitSelect}</InputLeftAddon>}

        <Input
          type="number"
          value={numericValue || ""}
          onChange={onChangeNumber}
          minWidth="75px"
          isDisabled={disabled}
          isReadOnly={readOnly}
        />

        {!prefixUnit && (
          <InputRightAddon pl={1} pr={1}>
            {unit ? <Text>{displayUnit}</Text> : $unitSelect}
          </InputRightAddon>
        )}
      </InputGroup>
    </>
  );
}
